import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Seo } from '@/components/Seo'
import { AuthShell, FormError } from '@/components/AuthShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSession } from '@/lib/session'
import { useI18n, translateError } from '@/i18n'
import { GoogleSignInButton } from '@/components/GoogleSignInButton'

/** SIGN IN. Twin of Register.tsx — change one, look at the other. */
export default function Login() {
  const { login } = useSession()
  const { t } = useI18n()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // Where the visitor was heading before being stopped (RequireAuth sets it).
  const next = params.get('next') || '/account'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await login(email, password)
      navigate(next, { replace: true })
    } catch (err) {
      setError(translateError(err, t, t('login.failed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Seo title={t('login.title')} noIndex />
      <AuthShell
        title={t('login.title')}
        subtitle={t('login.subtitle')}
        highlights={[
          t('login.highlight.cookie'),
          t('login.highlight.hash'),
          t('login.highlight.ownData'),
        ]}
        footer={
          <>
            {t('login.noAccount')}{' '}
            <Link to="/register" className="font-medium text-foreground underline underline-offset-4">
              {t('register.submit')}
            </Link>
          </>
        }
      >
        <div data-upbo-src="src/pages/Login.tsx:61" className="flex flex-col gap-4">
          <FormError message={error} />
          <GoogleSignInButton
            onSuccess={() => navigate(next, { replace: true })}
            onError={(err) => setError(err)}
          />

          <div data-upbo-src="src/pages/Login.tsx:68" className="relative my-1">
            <div data-upbo-src="src/pages/Login.tsx:69" className="absolute inset-0 flex items-center">
              <div data-upbo-src="src/pages/Login.tsx:70" className="w-full border-t border-border" />
            </div>
            <div data-upbo-src="src/pages/Login.tsx:72" className="relative flex justify-center text-xs uppercase">
              <span data-upbo-src="src/pages/Login.tsx:73" className="bg-card px-2 text-muted-foreground">{t('auth.orDivider')}</span>
            </div>
          </div>

          <form data-upbo-src="src/pages/Login.tsx:77" onSubmit={submit} className="flex flex-col gap-4">
            <div data-upbo-src="src/pages/Login.tsx:78" className="flex flex-col gap-2">
              <Label htmlFor="email">{t('field.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('field.emailPlaceholder')}
              />
            </div>
            <div data-upbo-src="src/pages/Login.tsx:90" className="flex flex-col gap-2">
              <Label htmlFor="password">{t('field.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={busy} className="mt-2 w-full">
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('login.submit')}
            </Button>
          </form>
        </div>
      </AuthShell>
    </>
  )
}
